import React, { Context } from 'react';
import { RenderCallback } from '@okapi/core';
import { Router, GlobalRouter } from '@okapi/router-react';
import { Helmet, Provider as HelmetProvider, HelmetContext } from '@okapi/helmet-react';
import { RemoteProvider } from '@navi-toolbox/ui-components';
import { Store } from 'typesafe-actions';
import { Provider, ReactReduxContextValue } from 'react-redux';
import { getBaseUrl, getEnv, getOrchestratorHost } from 'common/selectors';
import { getLang } from 'utils';
import { getProductId } from 'features/product-info';
import { CssBaseline } from 'fronton-react';
import { getGtmCode, getSeoProperties, getYaCode } from 'features/page-model/ducks/get-model';
import { HrefLangMeta } from 'features/href-lang-meta';
import { App } from 'app';
import { MetaRobots } from '@navi-toolbox/seo';
import { getYaSplitInfo } from 'features/page-model';
import { PdpJsonLdConnected, VideoObjectJsonLdConnected } from 'common/components/json-ld';

export type EntryProps = { remoteContext: RenderCallback['remoteContext'] } & (
  | ClientSideEntryProps
  | ServerSideEntryProps
);

type ClientSideEntryProps = {
  url?: never;
  store: Store;
  helmetContext?: HelmetContext;
  router: GlobalRouter;
};

type ServerSideEntryProps = {
  url: string;
  store: Store;
  helmetContext?: HelmetContext;
  router: GlobalRouter;
};

export function createEntry({ helmetContext, store, router, remoteContext }: EntryProps) {
  const state = store.getState();
  const ORCHESTRATOR_HOST = getOrchestratorHost(state);
  const { USE_REMOTES = true } = getEnv(state);
  const seoProperties = getSeoProperties(state);
  const currentUrl = getBaseUrl(state);
  const yaSplitInfo = getYaSplitInfo(state);

  return () => {
    const { orchestratorDomain, gtmCode, yaCode } = React.useMemo(() => {
      return {
        orchestratorDomain: new URL(ORCHESTRATOR_HOST).origin,
        gtmCode: getGtmCode(state),
        yaCode: getYaCode(state),
        productId: getProductId(state),
      };
    }, []);

    const lang = getLang();
    const context = helmetContext as HelmetContext & Context<ReactReduxContextValue>;
    return (
      <HelmetProvider context={helmetContext}>
        <Router router={router}>
          <>
            <Provider store={store}>
              <RemoteProvider active={USE_REMOTES} context={remoteContext}>
                <CssBaseline>
                  <App />
                  <PdpJsonLdConnected context={context} />
                  <VideoObjectJsonLdConnected context={context} />
                </CssBaseline>
              </RemoteProvider>
            </Provider>
            <MetaRobots content={seoProperties?.metaRobotsContent || []} context={context} />
            <HrefLangMeta currentPage={currentUrl} context={context} />
            <Helmet>
              <html lang={lang} />

              {/* <!-- Google Tag Manager --> */}
              {typeof gtmCode === 'string' && gtmCode.length > 0 && (
                <script>
                  {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtmCode}');`}
                </script>
              )}
              {/* <!-- End Google Tag Manager --> */}

              {/* <!-- Yandex Metrics --> */}
              <script>
                {`(function(m, e, t, r, i, k, a) {
                m[i] = m[i] || function() {
                  (m[i].a = m[i].a || []).push(arguments);
                };
                m[i].l = 1 * new Date();
                k = e.createElement(t), a = e.getElementsByTagName(t)[0], k.async = 1, k.src = r, a.parentNode.insertBefore(k, a);
              })(window, document, 'script', 'https://mc.yandex.ru/metrika/tag.js', 'ym'); ym(${yaCode}, 'init', {clickmap:true, trackLinks:true, accurateTrackBounce:true, trackHash:true, ecommerce:'yaLayer'});`}
              </script>
              {/* <!-- End Yandex Metrics --> */}

              {yaSplitInfo?.src && <script src={yaSplitInfo?.src} defer />}

              <meta
                name="viewport"
                content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no"
              />
              <meta content="#444b5b" name="theme-color" />
              <link href="//fonts.googleapis.com" rel="dns-prefetch" />
              <link href={orchestratorDomain} crossOrigin="anonymous" rel="preconnect" />
              <link href="https://cdn.lemanapro.ru/" crossOrigin="anonymous" rel="preconnect" />
              <link href="https://www.googletagmanager.com" crossOrigin="anonymous" rel="preconnect" />
              <link href="https://www.google-analytics.com" crossOrigin="anonymous" rel="preconnect" />
              <link href="https://fonts.gstatic.com" crossOrigin="anonymous" rel="preconnect" />
              <link
                rel="preload"
                href="https://static.lemanapro.ru/fronton/lm-main/demi-bold.woff2"
                as="font"
                crossOrigin="anonymous"
              />
              <link
                rel="preload"
                href="https://static.lemanapro.ru/fronton/lm-main/regular.woff2"
                as="font"
                crossOrigin="anonymous"
              />
              <link
                href="https://static.lemanapro.ru/fronton/lm-fonts.css"
                rel="preload"
                as="style"
                crossOrigin="anonymous"
                onLoad={"this.rel = 'stylesheet'" as never}
              />
            </Helmet>
          </>
        </Router>
      </HelmetProvider>
    );
  };
}
